// לקוח טיפוסי ל-api (09 §1): token מ-auth (רענון <5 דק'), 401 → ניסיון רענון אחד → /login?return=…
// שגיאות: רק textHe/kind מהשרת — לעולם לא stack/SQL; אין "אין הרשאה" כשהבעיה תשתית.
import type { z } from 'zod';
import { AuthExpired, getAccessToken, refresh, clearSession, sessionEndedByExpiry } from './auth.ts';
import { loginUrl } from './safeReturn.ts';
import {
  ApiErrorBody,
  ArchiveResponse,
  RenameResponse,
  ArtifactDetailResponse,
  ArtifactListResponse,
  ShareResponse,
  CreateRunResponse,
  LatestRunResponse,
  LookupResponse,
  PanelAi1,
  PublishResponse,
  PanelAi2,
  RunListResponse,
  RunResponse,
} from './contract.ts';
import { T } from './strings.ts';
import { fetchRetrying, TRANSIENT_STATUSES } from './updating.ts';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly kind: string,
    public readonly textHe: string,
  ) {
    super(textHe);
  }
}

export function goLogin() {
  const here = window.location.pathname + window.location.search;
  clearSession();
  // ניקוי הסשן כבר מנווט (RequireSession) ל-/login עם ה-return הנכון — לא דורסים אותו
  if (window.location.pathname === '/login' || here.startsWith('/login')) return;
  window.location.assign(loginUrl(here, sessionEndedByExpiry() ? 'session_expired' : undefined));
}

export async function authed(path: string, init: RequestInit = {}, retried = false): Promise<Response> {
  let token: string;
  try {
    token = await getAccessToken();
  } catch (e) {
    if (e instanceof AuthExpired) goLogin();
    throw new ApiError(401, 'unauthenticated', T.sessionExpired);
  }
  // deploy: GET על רשת/502/503/504 — ניסיון חוזר עם backoff וטוסט "מתעדכן…" (lib/updating.ts); POST — ניסיון אחד
  const r = await fetchRetrying(path, { ...init, headers: { ...(init.headers ?? {}), authorization: `Bearer ${token}` } }, () => new ApiError(0, 'network', T.network));
  if (r.status === 401 && !retried) {
    try {
      await refresh();
    } catch {
      goLogin();
      throw new ApiError(401, 'unauthenticated', T.sessionExpired);
    }
    return authed(path, init, true);
  }
  if (r.status === 401) {
    goLogin();
    throw new ApiError(401, 'unauthenticated', T.sessionExpired);
  }
  if (!r.ok) {
    const body = ApiErrorBody.safeParse(await r.json().catch(() => null));
    if (body.success) throw new ApiError(r.status, body.data.error.code, body.data.error.textHe);
    if (TRANSIENT_STATUSES.has(r.status)) throw new ApiError(r.status, 'updating', T.updatingError);
    throw new ApiError(r.status, 'server', T.serverError(r.status));
  }
  return r;
}

export async function json<S extends z.ZodType>(schema: S, path: string, init?: RequestInit): Promise<z.infer<S>> {
  const r = await authed(path, init);
  const parsed = schema.safeParse(await r.json().catch(() => null));
  if (!parsed.success) throw new ApiError(r.status, 'bad_response', T.badResponse);
  return parsed.data;
}

const post = (body: unknown): RequestInit => ({ method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) });
const enc = encodeURIComponent;

export const api = {
  listArtifacts: () => json(ArtifactListResponse, '/api/artifacts'),
  /** version — תצוגה מקדימה של גרסה שאינה החיה (artifacts-draft-preview; היוצר בלבד). */
  getArtifact: (slug: string, version?: number) => json(ArtifactDetailResponse, `/api/artifacts/${enc(slug)}${version ? `?version=${version}` : ''}`),
  publish: (slug: string, version: number) => json(PublishResponse, `/api/artifacts/${enc(slug)}/publish`, post({ version })),
  getShare: (slug: string) => json(ShareResponse, `/api/artifacts/${enc(slug)}/share`),
  putShare: (slug: string, body: { everyone: boolean; userIds: string[] }) =>
    json(ShareResponse, `/api/artifacts/${enc(slug)}/share`, { method: 'PUT', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) }),
  renameArtifact: (slug: string, body: { nameHe: string; descriptionHe: string }) =>
    json(RenameResponse, `/api/artifacts/${enc(slug)}`, { method: 'PATCH', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) }),
  archiveArtifact: (slug: string) => json(ArchiveResponse, `/api/artifacts/${enc(slug)}/archive`, post({})),
  listRuns: (slug: string) => json(RunListResponse, `/api/artifacts/${enc(slug)}/runs`),
  createRun: (slug: string, params: unknown, paramLabels?: Record<string, string>, version?: number) =>
    json(CreateRunResponse, `/api/artifacts/${enc(slug)}/runs`, post({ params, ...(paramLabels ? { paramLabels } : {}), ...(version ? { version } : {}) })),
  /** report@3: חיפוש בשדה בחירה עם חיפוש (בלי ריצה). version — של הטיוטה בתצוגה מקדימה. */
  lookup: (slug: string, param: string, q: string, version?: number) => json(LookupResponse, `/api/artifacts/${enc(slug)}/lookup/${enc(param)}`, post({ q, ...(version ? { version } : {}) })),
  /** report@2: המטמון לפרמטרים האלה, או null (אין / לא דוח במטמון — המסך רץ כמו היום). */
  latestRun: (slug: string, params: unknown) =>
    json(LatestRunResponse, `/api/artifacts/${enc(slug)}/runs/latest?params=${enc(JSON.stringify(params ?? {}))}`).catch((e: unknown) => {
      if (e instanceof ApiError && e.status === 404) return null;
      throw e;
    }),
  recompute: (slug: string, params: unknown, paramLabels?: Record<string, string>) =>
    json(CreateRunResponse, `/api/artifacts/${enc(slug)}/runs`, post({ params, force: true, ...(paramLabels ? { paramLabels } : {}) })),
  getRun: (id: string) => json(RunResponse, `/api/runs/${enc(id)}`).then((r) => r.run),
  cancelRun: (id: string) => authed(`/api/runs/${enc(id)}/cancel`, post({})).then(() => undefined),
  fetchFile: async (id: string) => (await authed(`/api/files/${enc(id)}`)).blob(),
  panelAi1: () => json(PanelAi1, '/api/panel/ai1'),
  panelAi2: () => json(PanelAi2, '/api/panel/ai2'),
};
